import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack, Typography, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { useRecoilValueLoadable } from 'recoil';
import { getWarehousesByRegion } from '../../../../atoms/Forecast';
import { saveForecast } from '../../../../repositories/HttpRepository';
import { ToastMessage } from '../../../../constants/toastMessage';
import { toast } from 'react-toastify';
import { createMapKey } from '../../utils/transformData';
import { formatAddress } from '../../../../utils/address';

// Format: [id] alias @ address
const getWarehouseLabel = (warehouse) => {
  if (!warehouse) return '';

  const address = formatAddress(warehouse.address);
  return `[${warehouse.id}] ${warehouse.alias} @ ${address}`;
};

const createRenderWarehouseInput = (warehouses) => (params) => (
  <TextField {...params} placeholder="Select warehouse" helperText={warehouses.length === 0 ? 'No warehouses found for this region' : ''} />
);

const renderWarehouseOption = (props, option) => (
  <li {...props} key={option.id}>
    {getWarehouseLabel(option)}
  </li>
);

const EditWarehouseModal = ({ open, onClose, data, zoneId, onSaveSuccess }) => {
  const warehousesLoadable = useRecoilValueLoadable(getWarehousesByRegion);
  const warehouses = warehousesLoadable.state === 'hasValue' ? warehousesLoadable.contents : [];

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    warehouse_id: '',
  });

  // Get selected warehouse object from id
  const selectedWarehouse = useMemo(() => {
    if (!formData.warehouse_id) return null;
    return warehouses.find((w) => w.id === formData.warehouse_id) || null;
  }, [formData.warehouse_id, warehouses]);

  // Update form data when data changes or modal opens
  useEffect(() => {
    if (open && data) {
      setFormData({
        warehouse_id: data?.warehouse_id || '',
      });
    }
  }, [open, data]);

  const handleWarehouseChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      warehouse_id: newValue?.id || '',
    }));
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    if (!zoneId || !data?.forecasts) return;

    setSaving(true);
    try {
      const payload = data?.forecasts?.map((forecast) => ({
        id: forecast.forecastId,
        week_day: forecast.week_day,
        groups: [
          {
            id: zoneId,
            warehouse_id: formData.warehouse_id,
          },
        ],
      }));

      const dataChanges = data?.forecasts?.map((forecast) => {
        return {
          mapKey: createMapKey(forecast.forecastId, forecast.week_day, zoneId),
          formData: {
            warehouse_id: formData.warehouse_id,
          },
        };
      });

      const response = await saveForecast(payload);

      if (response.ok) {
        toast.success(ToastMessage.UPDATED_SUCCESS);
        // Notify parent to update local state
        if (onSaveSuccess) {
          onSaveSuccess(dataChanges);
        }
        onClose();
      } else {
        // console.error('Failed to save warehouse:', response);
        toast.error(ToastMessage.UPDATED_FAIL);
      }
    } catch (error) {
      // console.error('Error saving warehouse:', error);
      toast.error(ToastMessage.UPDATED_FAIL);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Warehouse <Typography variant="body2">{data?.name}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              Warehouse
            </Typography>
            <Autocomplete
              size="small"
              options={warehouses}
              value={selectedWarehouse}
              onChange={handleWarehouseChange}
              getOptionLabel={getWarehouseLabel}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={warehouses.length === 0}
              renderInput={createRenderWarehouseInput(warehouses)}
              renderOption={renderWarehouseOption}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditWarehouseModal;
